import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Phone, PhoneOff, Mic, MicOff, Volume2, VolumeX, Settings, User, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SoftphonePage = () => {
  const { token, user } = useAuth();
  const [sipAccounts, setSipAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [sipPassword, setSipPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [callStatus, setCallStatus] = useState("idle"); // idle, connecting, ringing, in-call
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [callDuration, setCallDuration] = useState(0);
  const [isRegistered, setIsRegistered] = useState(false);
  const [error, setError] = useState(null);
  
  const audioRef = useRef(null);
  const callTimerRef = useRef(null);

  useEffect(() => {
    const fetchSIPAccounts = async () => {
      if (!token) return;
      
      try {
        const res = await axios.get(`${API}/user/sip`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.data.success) {
          setSipAccounts(res.data.accounts || []);
        }
      } catch (error) {
        console.error("Failed to fetch SIP accounts", error);
      }
    };
    
    fetchSIPAccounts();
  }, [token]);

  useEffect(() => {
    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, []);

  const handleRegister = async () => {
    if (!selectedAccount || !sipPassword) {
      toast.error("Please select an account and enter password");
      return;
    }
    
    // In a real implementation, you would initialize JsSIP/SIP.js here
    // For demo purposes, we'll simulate registration
    setError(null);
    toast.info("Connecting to SIP server...");
    
    // Simulate registration delay
    setTimeout(() => {
      setIsRegistered(true);
      toast.success("Connected to sip.cyberxcalls.com");
    }, 1500);
  };

  const handleCall = () => {
    if (!phoneNumber) {
      toast.error("Please enter a phone number");
      return;
    }
    
    if (!isRegistered) {
      toast.error("Please register first");
      return;
    }
    
    setCallStatus("connecting");
    setError(null);
    
    // Simulate call connection
    setTimeout(() => {
      setCallStatus("ringing");
      setTimeout(() => {
        setCallStatus("in-call");
        startCallTimer();
        toast.success("Call connected");
      }, 2000);
    }, 1000);
  };

  const handleHangup = () => {
    setCallStatus("idle");
    stopCallTimer();
    setCallDuration(0);
    toast.info("Call ended");
  };

  const startCallTimer = () => {
    callTimerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  };

  const stopCallTimer = () => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const dialPadButtons = [
    "1", "2", "3",
    "4", "5", "6",
    "7", "8", "9",
    "*", "0", "#"
  ];

  const handleDialPad = (digit) => {
    setPhoneNumber(prev => prev + digit);
  };

  return (
    <div data-testid="softphone-page">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">WebRTC Softphone</h1>
        <p className="text-slate-500 mt-1">Make calls directly from your browser</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Softphone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900 rounded-3xl p-6 text-white"
        >
          {/* Status */}
          <div className="text-center mb-6">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm ${
              isRegistered ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'
            }`}>
              <div className={`w-2 h-2 rounded-full ${isRegistered ? 'bg-green-400' : 'bg-slate-500'}`} />
              {isRegistered ? 'Connected' : 'Disconnected'}
            </div>
          </div>

          {/* Call Status Display */}
          <div className="text-center mb-6">
            {callStatus === "idle" ? (
              <div className="py-8">
                <div className="w-20 h-20 bg-slate-800 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <User className="w-10 h-10 text-slate-500" />
                </div>
                <p className="text-slate-400">Ready to call</p>
              </div>
            ) : (
              <div className="py-8">
                <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${
                  callStatus === "in-call" ? 'bg-green-500' : 'bg-red-500 animate-pulse'
                }`}>
                  <Phone className="w-10 h-10 text-white" />
                </div>
                <p className="text-xl font-semibold">{phoneNumber}</p>
                <p className="text-slate-400 mt-1">
                  {callStatus === "connecting" && "Connecting..."}
                  {callStatus === "ringing" && "Ringing..."}
                  {callStatus === "in-call" && formatDuration(callDuration)}
                </p>
              </div>
            )}
          </div>

          {/* Phone Number Input */}
          <div className="mb-6">
            <Input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Enter number (e.g., +1234567890)"
              className="bg-slate-800 border-slate-700 text-white text-center text-xl py-6"
              data-testid="phone-number-input"
            />
          </div>

          {/* Dial Pad */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {dialPadButtons.map((digit) => (
              <button
                key={digit}
                onClick={() => handleDialPad(digit)}
                className="py-4 bg-slate-800 hover:bg-slate-700 rounded-xl text-xl font-semibold transition-colors"
                disabled={callStatus !== "idle"}
              >
                {digit}
              </button>
            ))}
          </div>

          {/* Call Controls */}
          <div className="flex justify-center gap-4">
            {callStatus === "idle" ? (
              <Button
                onClick={handleCall}
                disabled={!isRegistered}
                className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600"
                data-testid="call-btn"
              >
                <Phone className="w-6 h-6" />
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => setIsMuted(!isMuted)}
                  className={`w-12 h-12 rounded-full ${isMuted ? 'bg-red-500' : 'bg-slate-700 hover:bg-slate-600'}`}
                >
                  {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </Button>
                <Button
                  onClick={handleHangup}
                  className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600"
                  data-testid="hangup-btn"
                >
                  <PhoneOff className="w-6 h-6" />
                </Button>
                <Button
                  onClick={() => setIsSpeakerOn(!isSpeakerOn)}
                  className={`w-12 h-12 rounded-full ${!isSpeakerOn ? 'bg-red-500' : 'bg-slate-700 hover:bg-slate-600'}`}
                >
                  {isSpeakerOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                </Button>
              </>
            )}
          </div>

          {/* Audio Element */}
          <audio ref={audioRef} autoPlay />
        </motion.div>

        {/* Settings Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-6"
        >
          {/* SIP Configuration */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="w-5 h-5 text-slate-500" />
              <h3 className="font-semibold text-slate-900">SIP Configuration</h3>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Select SIP Account</Label>
                <Select 
                  value={selectedAccount?.name || ""} 
                  onValueChange={(v) => setSelectedAccount(sipAccounts.find(a => a.name === v))}
                >
                  <SelectTrigger className="mt-1" data-testid="sip-account-select">
                    <SelectValue placeholder="Choose account" />
                  </SelectTrigger>
                  <SelectContent>
                    {sipAccounts.map((acc) => (
                      <SelectItem key={acc.id || acc.name} value={acc.name}>
                        {acc.name}@sip.cyberxcalls.com
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>SIP Password</Label>
                <Input
                  type="password"
                  value={sipPassword}
                  onChange={(e) => setSipPassword(e.target.value)}
                  placeholder="Enter SIP password"
                  className="mt-1"
                  data-testid="sip-password-input"
                />
              </div>

              <div className="bg-slate-50 rounded-lg p-4 text-sm">
                <p className="font-medium text-slate-700 mb-2">Server Details:</p>
                <p className="text-slate-600">Domain: sip.cyberxcalls.com</p>
                <p className="text-slate-600">WebSocket: wss://sip.cyberxcalls.com:8089/ws</p>
              </div>

              <Button
                onClick={handleRegister}
                disabled={isRegistered}
                className={`w-full ${isRegistered ? 'bg-green-600' : 'bg-red-600 hover:bg-red-700'}`}
                data-testid="register-btn"
              >
                {isRegistered ? 'Connected' : 'Connect'}
              </Button>
            </div>
          </div>

          {/* Info */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-800">Browser Requirements</h4>
                <p className="text-sm text-amber-700 mt-1">
                  WebRTC calling requires a modern browser (Chrome, Firefox, Edge) and microphone permission.
                  Ensure your SIP server supports WebSocket connections.
                </p>
              </div>
            </div>
          </div>

          {/* Balance */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-4">Account Balance</h3>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Available Credit</span>
              <span className="text-2xl font-bold text-green-600">
                ${parseFloat(user?.credit || 0).toFixed(2)}
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SoftphonePage;
